import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './styles.scss';
import { MinusIcon, PlusIcon, InfoIcon, HeartStraightIcon } from '@phosphor-icons/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { removeWishListItem, setQuickAddItem, showPreview, updateCart, updateWishList, updateProductsData, updateSlabsData, updateBasesData, updateFinishesData } from '../../slices/app-slice';
import { updateHeader } from '../../slices/header-slice';
import { getProducts, getSlabs, getBases, getFinishesData } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';

import 'swiper/css';
import 'swiper/css/navigation';

import Animate from '../../components/animate';
import { Navigation } from 'swiper/modules';
import { useDispatch, useSelector } from 'react-redux';
import Item from '../../components/item';
import VideoPlayer from '../../components/video-player';
import { formatPrice } from '../../utility/utils';

import instagram from '../../assets/images/footer/instagram.svg';
import facebook from '../../assets/images/footer/facebook.svg';
import youtube from '../../assets/images/footer/youtube.svg';
import pinterest from '../../assets/images/footer/pinterest.svg';
import tiktok from '../../assets/images/footer/tiktok.svg';
import FeedbackModal from '../../components/feedback-modal';
import ReactImageMagnify from 'react-image-magnify';
import SEO from '../../components/seo';
import GalleryImage from '../../components/gallery-image';
import Tooltip from '../../components/tooltip';
import commentsIcon from '../../assets/images/icons/comment.svg';

const Product = ({ type }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id, variantId } = useParams();
    const announcement = useSelector((state) => state.app.homepage?.announcement);

    const [quantity, setQuantity] = useState(1);
    const [showReadMore, setShowReadMore] = useState(false);
    const [finishOption, setFinishOption] = useState(null);

    const [sizeOptions, setSizeOptions] = useState([]);
    const [sizeRequired, setSizeRequired] = useState(true);
    const [showMagnify, setShowMagnify] = useState(false);
    const [recommendedProducts, setRecommendedProducts] = useState([])
    const [product, setProduct] = useState({})

    const [preloadedImages, setPreloadedImages] = useState(null);
    const [isPreloading, setIsPreloading] = useState(true);
    const [isWishlistItem, setIsWishListItem] = useState(false);
    const [showSeating, setShowSeating] = useState(false);
    const [showFinishes, setShowFinishes] = useState(false);

    // Loading state for data fetching
    const [isDataLoading, setIsDataLoading] = useState(false);
    const hasFetched = useRef(false);

    const wishlist = useSelector((state) => state.app.wishList)
    const products = useSelector((state) => state.app.products);
    const slabs = useSelector((state) => state.app.slabs);
    const bases = useSelector((state) => state.app.bases);
    const finishesData = useSelector((state) => state.app.finishes);

    // Register loading state with global spinner
    useDataLoading(`${type}-data`, isDataLoading, []);

    // Fetch data based on type if not already populated
    useEffect(() => {
        const fetchData = async () => {
            let hasData = false;
            let apiCall = null;
            let updateAction = null;

            // Determine which data to fetch based on type
            switch (type) {
                case 'products':
                    hasData = products && Array.isArray(products) && products.length > 0;
                    apiCall = getProducts;
                    updateAction = updateProductsData;
                    break;
                case 'slabs':
                    hasData = slabs && Array.isArray(slabs) && slabs.length > 0;
                    apiCall = getSlabs;
                    updateAction = updateSlabsData;
                    break;
                case 'bases':
                    hasData = bases && Array.isArray(bases) && bases.length > 0;
                    apiCall = getBases;
                    updateAction = updateBasesData;
                    break;
                default:
                    return; // Unknown type, don't fetch anything
            }

            const hasFinishesData = finishesData && Array.isArray(finishesData) && finishesData.length > 0;
            const needsFinishes = type === 'bases' && !hasFinishesData;

            if ((!hasData || needsFinishes) && !hasFetched.current && apiCall && updateAction) {
                hasFetched.current = true;
                setIsDataLoading(true);
                try {
                    const promises = [];

                    if (!hasData) {
                        promises.push(apiCall());
                    }

                    if (needsFinishes) {
                        promises.push(getFinishesData());
                    }

                    const responses = await Promise.all(promises);

                    let responseIndex = 0;
                    if (!hasData) {
                        dispatch(updateAction(responses[responseIndex].data));
                        responseIndex++;
                    }

                    if (needsFinishes) {
                        dispatch(updateFinishesData(responses[responseIndex].data));
                    }
                } catch (error) {
                    console.error(`Error fetching ${type} data:`, error);
                } finally {
                    setIsDataLoading(false);
                }
            }
        };

        fetchData();
    }, [type, products, slabs, bases, finishesData, dispatch]);

    const preloadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve({ src, success: true });
            img.onerror = () => resolve({ src, success: false });
        });
    };

    useEffect(() => {
        if (product?.images) {
            setIsPreloading(true);
            setPreloadedImages(null)

            const preloadPromises = product.images.map((img) => {
                const imageSrc = `${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/${type}/${img?.fileName}&width=1200&height=1200&quality=99&format=webp`;

                return Promise.all([
                    preloadImage(imageSrc),
                ]).then(() => ({
                    imageSrc
                }));
            });

            Promise.all(preloadPromises)
                .then((results) => {
                    const images = results.reduce((acc, result, i) => {
                        acc[i] = result;
                        return acc;
                    }, {});
                    setPreloadedImages(images);
                    setIsPreloading(false);
                })
                .catch((error) => {
                    console.error('Error preloading images:', error);
                });
        }
    }, [product, type]);

    const handleQuantityClick = useCallback((val) => {
        if (!product) return; // Safety check

        if (!sizeRequired) {
            if (product.stock) {
                if (val <= product.stock) {
                    setQuantity(val < 0 ? 0 : val)
                } else {
                    setQuantity(product.stock)
                }
            } else {
                setQuantity(val < 0 ? 0 : val)
            }
        }
    }, [product, sizeRequired])

    const addToWishList = (event, isWishlistItem = false) => {
        event.stopPropagation();

        if (!product?.id) return; // Safety check

        if (isWishlistItem) {
            dispatch(removeWishListItem(product.id))
        } else {
            dispatch(updateWishList({
                item: product,
                type: type
            }))

            dispatch(setQuickAddItem({
                item: product
            }))

            dispatch(showPreview(true))
        }
    }

    useEffect(() => {
        let array = [];
        let items = null;

        // Get the correct data array based on type
        switch (type) {
            case 'products':
                items = products;
                break;
            case 'slabs':
                items = slabs;
                break;
            case 'bases':
                items = bases;
                break;
            default:
                return;
        }

        if (items && Array.isArray(items)) {
            const item = items.find(x => x.id === parseInt(id))

            if (item?.variants) {
                if (item.variants.length <= 4) {
                    item.variants.slice(0, item.variants.length).map((v) => array.push(v))
                    items.filter(x => (x.id !== item.id && x.title !== item.title)).slice(0, (4 - item.variants.length)).map((p) => array.push(p))
                }
            } else {
                items.filter(x => (x.id !== item.id && x.title !== item.title)).slice(0, 4).map((p) => array.push(p))
            }
        }

        const isOnWishlist = product?.id && product?.title ?
            wishlist.filter(x => x.item.id === product.id && x.item.title === product.title).length > 0 :
            false;

        setIsWishListItem(isOnWishlist)
        setRecommendedProducts(array)
    }, [products, slabs, bases, id, type, wishlist, product?.id, product?.title]);

    useEffect(() => {
        dispatch(updateHeader('light'))
    }, [dispatch]);

    //IF base product changes
    useEffect(() => {
        if (type && id) {
            let items = null;

            // Get the correct data array based on type
            switch (type) {
                case 'products':
                    items = products;
                    break;
                case 'slabs':
                    items = slabs;
                    break;
                case 'bases':
                    items = bases;
                    break;
                default:
                    return;
            }

            if (items && Array.isArray(items)) {
                let p = items.find(x => x.id === parseInt(id))

                if (type === "products") {
                    setProduct(p)
                    setShowMagnify(true)
                    setSizeRequired(false)

                    if (p?.variants) {
                        const options = p?.variants?.filter(x => x.type === "Size")

                        if (options?.length > 0) {
                            setSizeRequired(true)
                        } else {
                            setSizeRequired(false)
                        }

                        setSizeOptions(options)
                    }
                } else {
                    setProduct(p)
                }
            }
        }
    }, [type, id, products, slabs, bases]);

    //IF the variant changes
    useEffect(() => {
        if (variantId) {
            let items = null;

            // Get the correct data array based on type
            switch (type) {
                case 'products':
                    items = products;
                    break;
                case 'slabs':
                    items = slabs;
                    break;
                case 'bases':
                    items = bases;
                    break;
                default:
                    return;
            }

            if (items && Array.isArray(items)) {
                if (type === "products") {
                    let p = items.find(x => x.id === parseInt(id))

                    const options = p?.variants?.filter(x => x.type === "Size")
                    setSizeOptions(options)

                    const variant = p?.variants?.find(x => x.id === parseInt(variantId))

                    if (variant?.type === "Size") {
                        setSizeRequired(false)
                    } else {
                        setSizeRequired(true)
                    }

                    setProduct(variant)
                    setShowMagnify(true)
                }
            }
        }
    }, [variantId, type, id, products, slabs, bases]);

    const addToCart = useCallback(() => {
        if (!product?.price) return; // Safety check

        dispatch(updateCart({
            product: product,
            quantity: quantity,
            total: product.price * quantity
        }))

        dispatch(setQuickAddItem({
            product: product,
            quantity: 1,
            total: product.price
        }))

        dispatch(showPreview(true))
    }, [dispatch, product, quantity])

    const inquire = useCallback(() => {
        if (!product) return; // Safety check
        navigate('/contact', { state: { product: product, finish: finishOption } });
    }, [navigate, product, finishOption])

    const onProductClick = (item) => {
        if (item.productId) {
            navigate({
                pathname: `/products/${item.id}/${parseInt(item.productId)}`,
                search: `?title=${item.title}`,
            })
        } else {
            if (type === "bases") {
                navigate({
                    pathname: `/bases/${item.id}`,
                    search: `?title=${item.title}`,
                })
            } else if (type === "slabs") {
                navigate({
                    pathname: `/slabs/${item.id}`,
                    search: `?title=${item.title}`,
                })
            } else {
                navigate({
                    pathname: `/products/${item.id}`,
                    search: `?title=${item.title}`,
                })
            }
        }
        window.scrollTo(0, 0);
    }

    const onFeedbackClose = () => {
        setShowReadMore(false)
    }

    const setSizeOption = (e) => {
        const { value } = e.target;
        let items = null;

        // Get the correct data array based on type
        switch (type) {
            case 'products':
                items = products;
                break;
            case 'slabs':
                items = slabs;
                break;
            case 'bases':
                items = bases;
                break;
            default:
                return;
        }

        const item = items?.find(x => x.id === parseInt(id))

        if (value) {
            navigate(`/products/${item.id}/${parseInt(value)}`);
        } else {
            navigate(`/products/${item.id}`);
            setSizeRequired(true)
        }
    }

    return (
        <>
            {product &&
                <>
                    {product?.metaData?.seoData &&
                        <SEO
                            title={product.metaData.seoData.title ? product.metaData.seoData.title : "Paramount Wood Co."}
                            description={product.metaData.seoData.description ? product.metaData.seoData.description : ""}
                            name="Paramount Wood Co."
                            type="website"
                            url={window.location.href}
                        />
                    }
                    <div className={`product-page ${announcement ? 'has-announcement' : ''}`}>
                        <Animate>
                            <div className='product'>
                                <div className="product-gallery">
                                    {product?.images &&
                                        <Swiper
                                            slidesPerView={1}
                                            modules={[Navigation]}
                                            navigation={true}
                                            loop
                                        >
                                            {product?.images.map((img, i) => (
                                                <SwiperSlide key={i}>
                                                    {isPreloading ? (
                                                        <div className='image-loader'>
                                                            <div className='loader'></div>
                                                        </div>

                                                    ) : (
                                                        <GalleryImage src={preloadedImages[i]?.imageSrc} index={i} description={img?.description} />
                                                    )}
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    }
                                </div>

                                <div className="product-info">
                                    {(type === "products") ?
                                        <>
                                            <p className='product-category'>{product?.category}</p>
                                            <h1>{product?.title}</h1>

                                            <div className='price-wrapper'>
                                                {product.salePrice
                                                    ? <> <p className='strike-through'>{formatPrice(product?.price)}</p> <p className='sale-price'>{formatPrice(product?.salePrice)}</p><div className="pill coral">Sale</div></>
                                                    : <p className='product-price'>{formatPrice(product?.price)}</p>
                                                }
                                            </div>


                                            {product.stock > 0 &&
                                                <div className='product-quantity-wrapper'>
                                                    <div className='product-quantity'>
                                                        <div className='circle-btn' onClick={() => handleQuantityClick(quantity - 1)}><MinusIcon size={20} /></div>
                                                        <div>{quantity}</div>
                                                        <div className='circle-btn' onClick={() => handleQuantityClick(quantity + 1)}><PlusIcon size={20} /></div>
                                                    </div>

                                                    <div className='stock-indicator'><p>({product.stock} in stock)</p></div>
                                                </div>
                                            }

                                            <div className='product-actions mt-4'>
                                                {product.stock > 0 && sizeOptions?.length > 0 &&
                                                    <select onChange={(e) => setSizeOption(e)}>
                                                        <option value="">Select size</option>
                                                        {sizeOptions?.map((s) => (
                                                            <option value={s.id}>{s.title}</option>
                                                        ))}
                                                    </select>
                                                }

                                                {product.stock > 0
                                                    ? <button className='cta gold-fill' disabled={sizeRequired || !quantity} onClick={() => addToCart()}>Add to cart</button>
                                                    : <button className='cta gold-fill' onClick={() => inquire()}>Order this</button>
                                                }

                                                <HeartStraightIcon size={32} onClick={(e) => addToWishList(e, isWishlistItem)} className={`wishlist-icon ${isWishlistItem ? "full" : ""}`} />
                                            </div>
                                        </>
                                        :
                                        <>

                                            {product.visibility === "Sale Pending" &&
                                                <div className='pill forest'>Sale Pending</div>
                                            }
                                            <h1>{finishOption ? finishOption : ""} {product?.title}</h1>

                                            <div className='product-actions mt-4'>
                                                {product.visibility === "Sale Pending"
                                                    ? <button className='cta gold-fill' onClick={() => inquire()}>Request Similar</button>
                                                    : <button className='cta gold-fill' onClick={() => inquire()}>Inquire for price</button>
                                                }
                                                <HeartStraightIcon size={32} onClick={(e) => addToWishList(e, isWishlistItem)} className={`wishlist-icon ${isWishlistItem ? "full" : ""}`} />
                                            </div>
                                        </>
                                    }

                                    <div className='socials'>
                                        <a href='https://www.instagram.com/paramountwoodco/' rel="noreferrer" target='_blank'><img src={instagram} alt='Paramount Wood Co. Instagram' /></a>
                                        <a href='https://www.facebook.com/ParamountWoodCo/' rel="noreferrer" target='_blank'><img src={facebook} alt='Paramount Wood Co. Facebook' /></a>
                                        <a href='https://www.youtube.com/@paramountwoodco' rel="noreferrer" target='_blank'><img src={youtube} alt='Paramount Wood Co. Youtube' /></a>
                                        <a href='https://pinterest.com/paramountwoodcoshop/' rel="noreferrer" target='_blank'><img src={pinterest} alt='Paramount Wood Co. Pinterest' /></a>
                                        <a href='https://www.tiktok.com/@paramountwoodco' rel="noreferrer" target='_blank'><img src={tiktok} alt='Paramount Wood Co. TikTok' /></a>
                                    </div>

                                    <div className='product-description'>
                                        {type === "bases" && (product?.material !== "Wood" && product?.material !== "Other") &&
                                            <>
                                                <div className='base-finish-title'><h3>Base finish options</h3><div className='text-link' onClick={(e) => setShowFinishes(true)}>Preview finishes</div></div>
                                                <div className='base-finish-options'>
                                                    {finishesData?.map((option, i) => (
                                                        <Tooltip key={i} position='bottom' hideTriangle={true} text={option?.name}>
                                                            <div className={`option ${option?.name === finishOption ? "active" : ""}`} onClick={() => setFinishOption(option?.name)}>
                                                                {option?.imageFileName ? (
                                                                    <img src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/finishes/${option.imageFileName}&width=50&height=50&quality=99&format=webp`} alt={option?.name} />
                                                                ) : (
                                                                    <div className='no-image'></div>
                                                                )}
                                                            </div>
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            </>
                                        }

                                        {product.customShipping &&
                                            <div className='info'>
                                                <InfoIcon size={20} /><p>This item requires&nbsp;<span onClick={() => setShowReadMore(true)}>specialized shipping</span></p>
                                            </div>
                                        }

                                        {product.videos?.length > 0 &&
                                            <VideoPlayer url={product.videos[0].url} />
                                        }

                                        {product?.inventoryId &&
                                            <>
                                                <h3>Inventory ID {product.inventoryId && <div className='text-link' onClick={(e) => setShowSeating(true)}>Seating Chart</div>}</h3>
                                                <div><p>{product?.inventoryId}</p></div>
                                            </>
                                        }

                                        <>
                                            <h3>Specification {!product.inventoryId && <div className='text-link' onClick={(e) => setShowSeating(true)}>Seating Chart</div>}</h3>
                                            {/* {product?.width && product?.length && product?.height &&
                                                <div className='specification-dimensions'>
                                                    <p>Width: <span>{product?.width}″</span></p>
                                                    <p>Length: <span>{product?.length}″</span></p>
                                                    <p>Thickness: <span>{product?.height}″</span></p>
                                                </div>
                                            }

                                            {product.seats &&
                                                <div className='specification-dimensions'>
                                                    <p>Seats: <span>{product.seats}</span></p>
                                                </div>
                                            } */}

                                            {product?.specification &&
                                                <div className='html-text' dangerouslySetInnerHTML={{ __html: product?.specification }} />
                                            }
                                        </>


                                        {product?.comments &&
                                            <div className='product-comments'>
                                                <h3 className='comment-title'><img src={commentsIcon} alt='Lisas comments' />Lisa's Comments</h3>
                                                <div>"{product.comments}"</div>
                                            </div>
                                        }

                                        {product?.grain &&
                                            <>
                                                <h3>Grain</h3>
                                                <div className='html-text' dangerouslySetInnerHTML={{ __html: product?.grain }} />
                                            </>
                                        }

                                        {(product?.description) &&
                                            <>
                                                <h3>Description</h3>
                                                {product.description && <div className='html-text' dangerouslySetInnerHTML={{ __html: product?.description }} />}
                                            </>
                                        }

                                    </div>
                                </div>
                            </div>
                        </Animate>

                        {recommendedProducts?.length > 0 &&
                            <div className='product-other'>
                                <h2>You may also like</h2>
                                <div className='items'>
                                    {recommendedProducts.map((item, i) => (
                                        <Item key={i} item={item} index={i} setItem={onProductClick} type={type} />
                                    ))}
                                </div>
                            </div>
                        }
                    </div>

                    {showReadMore &&
                        <FeedbackModal status={""} type={"info"} title={"Specialized Shipping"} message={"Some items are too large and heavy for conventional shipping methods. After your purchase, we will contact you to arrange an alternative at a competitive price."} onClose={onFeedbackClose} label={"Close"} />
                    }
                </>
            }

            {showSeating &&
                <div className='overlay' onClick={(e) => {
                    e.stopPropagation();
                    setShowSeating(false)
                }}>
                    <div className='overlay-content' onClick={(e) => {
                        e.stopPropagation();
                    }}>
                        <img alt='seating plan' src={process.env.PUBLIC_URL + '/images/hosted/seating_plan.jpg'} />
                        <button className='cta' onClick={() => setShowSeating(false)}>Close</button>
                    </div>

                </div>

            }

            {showFinishes &&
                <div className='overlay' onClick={(e) => {
                    e.stopPropagation()
                    setShowFinishes(false)
                }}>
                    <div className='overlay-content' onClick={(e) => {
                        e.stopPropagation()
                    }}>
                        <div className='finish-modal'>
                            {finishesData?.map((option, i) => (
                                <div key={i} className='finish'>
                                    <img src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/finishes/${option?.imageFileName}&width=200&height=200&quality=99&format=webp`} alt={option?.name} />
                                    <p>{option?.name}</p>
                                </div>
                            ))}

                        </div>
                        <button className='cta' onClick={() => setShowFinishes(false)}>Close</button>
                    </div>

                </div>

            }
        </>
    )
}

export default Product;